import React, { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

/**
 * Tilt3D — wraps children with a 3D tilt-on-mouse effect.
 * Uses perspective + rotateX/rotateY. Lightweight (no three.js).
 */
export function Tilt3D({ children, className = "", max = 12, glare = true, ...rest }) {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 200, damping: 20 });
  const sy = useSpring(my, { stiffness: 200, damping: 20 });
  const rotateY = useTransform(sx, [-0.5, 0.5], [-max, max]);
  const rotateX = useTransform(sy, [-0.5, 0.5], [max, -max]);
  const glareBg = useTransform(
    [sx, sy],
    ([x, y]) => `radial-gradient(circle at ${(x + 0.5) * 100}% ${(y + 0.5) * 100}%, rgba(255,255,255,0.35), transparent 45%)`
  );

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ perspective: 1000 }}
      className={className}
      {...rest}
    >
      <motion.div
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        className="relative w-full h-full"
      >
        {children}
        {glare && (
          <motion.div
            className="pointer-events-none absolute inset-0 rounded-[inherit] opacity-0 hover:opacity-100 transition-opacity"
            style={{ background: glareBg }}
          />
        )}
      </motion.div>
    </motion.div>
  );
}

/**
 * FloatingOrb — decorative animated blur circles for depth.
 */
export function FloatingOrb({ className = "", delay = 0, size = 300, color = "#FFB7B2" }) {
  return (
    <motion.div
      aria-hidden
      className={`absolute rounded-full blur-3xl pointer-events-none ${className}`}
      style={{ width: size, height: size, background: color, opacity: 0.35 }}
      animate={{ y: [0, -30, 0], x: [0, 20, 0], scale: [1, 1.1, 1] }}
      transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay }}
    />
  );
}

/**
 * ParallaxImage — mouse-follow parallax with soft 3D lift.
 */
export function ParallaxImage({ src, alt, className = "" }) {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 120, damping: 20 });
  const sy = useSpring(my, { stiffness: 120, damping: 20 });
  const rY = useTransform(sx, [-0.5, 0.5], [-8, 8]);
  const rX = useTransform(sy, [-0.5, 0.5], [8, -8]);
  const tx = useTransform(sx, [-0.5, 0.5], [-15, 15]);
  const ty = useTransform(sy, [-0.5, 0.5], [-15, 15]);

  return (
    <motion.div
      ref={ref}
      onMouseMove={(e) => {
        const r = ref.current.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
      }}
      onMouseLeave={() => { mx.set(0); my.set(0); }}
      style={{ perspective: 1200 }}
      className={className}
    >
      <motion.img
        src={src} alt={alt}
        style={{ rotateX: rX, rotateY: rY, x: tx, y: ty, transformStyle: "preserve-3d" }}
        className="w-full h-full object-cover rounded-[3rem] shadow-2xl"
      />
    </motion.div>
  );
}
